import type { ChartTheme, Theme } from '@/types'

/**
 * Get chart theme configuration based on current theme
 */
export const getChartTheme = (theme: Theme): ChartTheme => {
  if (theme === 'light') {
    return {
      backgroundColor: '#ffffff',
      textColor: '#1e293b',
      gridColor: 'rgba(0, 0, 0, 0.05)',
      tooltipBg: '#ffffff',
      tooltipText: '#1e293b',
      withAdviceColor: '#14b8a6',
      withoutAdviceColor: '#94a3b8',
    }
  }

  return {
    backgroundColor: '#ffffff',
    textColor: '#1e293b',
    gridColor: 'rgba(0, 0, 0, 0.05)',
    tooltipBg: '#ffffff',
    tooltipText: '#1e293b',
    withAdviceColor: '#0f766e',
    withoutAdviceColor: '#94a3b8',
  }
}

/**
 * Common chart options for all charts
 */
export const getCommonChartOptions = (theme: ChartTheme) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        color: theme.textColor,
        usePointStyle: true,
        padding: 20,
        font: {
          family: "'Source Sans 3', sans-serif",
        },
      },
    },
    tooltip: {
      backgroundColor: theme.tooltipBg,
      titleColor: theme.tooltipText,
      bodyColor: theme.tooltipText,
      borderColor: theme.gridColor,
      borderWidth: 1,
      padding: 12,
      titleFont: {
        family: "'Source Sans 3', sans-serif",
        weight: 600,
      },
      bodyFont: {
        family: "'Source Sans 3', sans-serif",
      },
    },
  },
})

/**
 * Convert hex color to rgba
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Generate a muted/desaturated version of a color
 */
export const getMutedColor = (hex: string, theme: Theme): string => {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  // Desaturate and adjust lightness
  const mutedS = s * 0.2
  const mutedL = theme === 'light' ? 0.65 : 0.4
  const hue = h * 360

  const a = mutedS * Math.min(mutedL, 1 - mutedL)
  const f = (n: number) => {
    const k = (n + hue / 30) % 12
    return mutedL - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
  }

  const toHex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`
}

/**
 * Generate brand color palette for donut chart
 */
export const generateBrandPalette = (primaryColor: string, count: number): string[] => {
  const colors: string[] = [primaryColor]

  // Parse primary color to HSL
  const r = parseInt(primaryColor.slice(1, 3), 16) / 255
  const g = parseInt(primaryColor.slice(3, 5), 16) / 255
  const b = parseInt(primaryColor.slice(5, 7), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  const hslToHex = (hue: number, sat: number, lit: number) => {
    sat /= 100
    lit /= 100
    const a = sat * Math.min(lit, 1 - lit)
    const f = (n: number) => {
      const k = (n + hue / 30) % 12
      return lit - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    }
    const toHex = (x: number) =>
      Math.round(x * 255)
        .toString(16)
        .padStart(2, '0')
    return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`
  }

  // Generate additional colors by rotating hue
  const hueStep = 360 / count
  for (let i = 1; i < count; i++) {
    const newHue = (h * 360 + hueStep * i) % 360
    colors.push(hslToHex(newHue, s * 100 * 0.9, l * 100))
  }

  return colors
}
